import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';

import QueryString from 'query-string';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';

// Server
import { listBuildings, getFloor, setRoomStatus, getRoom } from '../../../api';

// Components
import RoomInfoPanel from '../../../components/RoomInfoPanel';
import DefaultInfoPanel from '../../../components/DefaultInfoPanel';
import PageHeader from '../../../components/PageHeader';
import StatusLegend from '../../../components/StatusLegend';
import DetailAside from '../../../components/DetailAside';
import MapRoomView from './MapRoomView';
import SelectorWidget from './SelectorWidget';
import RoomList from './RoomList';

// Constants
import { AlertTypes, isNull } from '../../../constants';
import { T, SPLIT_WIDTH } from '../../../constants/tokens';
import { removeAnnouncement } from '../../../redux/actions/announcementActions';

class MapView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedRoom: null,
      buildings: null,
      school: null,
      selectedBuilding: null,
      selectedFloorID: null,
      selectedFloor: null,
      selectedFloorImage: null,
      lastImageTime: null,
      isLoadingFloor: false
    };

    this.mapRef = React.createRef();
  }

  componentWillUnmount() {
    this._ismounted = false;
    clearInterval(this.schoolTimer);
    clearInterval(this.floorTimer);
    this.schoolTimer = null;
    this.floorTimer = null;
  }

  componentDidMount() {
    this._ismounted = true;
    this.getSchoolFromServer();
    this.startSchoolTimer();
  }

  componentDidUpdate() {
    // On first load, isLoggedIn is false when mounting, so the componentDidMount call doesn't work
    if (this._ismounted && this.props.isLoggedIn && !this.state.buildings) {
      this.getSchoolFromServer();
    }
  }

  forceOpenRoom = () => {
    // See if there's an emergency to focus the map on
    let roomID = QueryString.parse(this.props.location?.search)?.roomID;
    if (roomID != null) this.openRoomByID(roomID);
  }

  // Open the building and floor a room is on, and select it there
  openRoomByID = (roomID) => {
    getRoom(roomID).then(room => {
      let building = this.state.buildings?.find(building => building.getID() === room?.getFloor()?.getBuildingID());
      this.setState({ selectedBuilding: building });
      this.onFloorSelection(room.getFloorID(), room);
    }).catch(error => {
      console.log(error);
    });
  }

  // Called when a room is chosen from the list under the map. The list is the
  // floor on screen, so the room is selected right there, and the map is
  // brought back into view, since the list sits below it.
  onRoomListSelection = (room) => {
    const onThisFloor = this.state.selectedFloor?.getRooms()?.find(r => r.getID() === room.getID());
    this.setState({ selectedRoom: onThisFloor ?? room });

    this.mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Keep trying to get the school object if not successful
  startSchoolTimer = () => {
    this.schoolTimer = setInterval(
      () => {
        if (this.state.buildings) {
          clearInterval(this.schoolTimer);
          this.schoolTimer = null;
        }
        else this.getSchoolFromServer();
      },
      5000
    );
  }

  // Called when a user selects a room on the map
  onRoomSelection = (room) => {
    this.setState({ selectedRoom: this.state.selectedRoom === room ? null : room });
  }

  // Called when a user selects a floor from the selector widget
  onFloorSelection = (selectedFloorID, room = null) => {
    this.setState({ selectedFloorID: selectedFloorID, selectedFloorImage: null }, () => {
      this.getFloorFromServer(room);
    });
  }

  // Retrieves the school object from the server
  getSchoolFromServer() {
    if (this.props.isLoggedIn) {
      listBuildings().then(buildings => {
        if (this._ismounted) {
          this.setState({ buildings: buildings, selectedBuilding: buildings?.length > 0 ? buildings[0] : null }, () => {
            this.forceOpenRoom();
          });
        }
      }).catch(error => {
        if (this._ismounted) {
          this.props.setError(error ? error : 'Could not retrieve the buildings from the server.');
        }
      });
    }
  }

  // Retrieves the floor object from the server
  getFloorFromServer(room = null) {
    this.setState({ isLoadingFloor: true });
    getFloor(this.state.selectedFloorID).then(floor => {
      if (this._ismounted) {
        if (!this.state.selectedFloorImage || this.state.lastImageTime == null || new Date() - this.state.lastImageTime > 1000 * 60 * 5) {
          this.setState({ selectedFloorImage: floor.getFloorPlanImage(), lastImageTime: new Date() });
          console.log('image update');
        }
        this.setState({ selectedFloor: floor, isLoadingFloor: false });
        if (room != null) this.onRoomSelection(floor.getRooms()?.find(r => r.getID() === room.getID()));
        else if (this.state.selectedRoom) this.setState({ selectedRoom: floor.getRooms().find(room => room.getID() === this.state.selectedRoom.getID()) || null });
        this.updateFloorFromServer();
      }
    }).catch(error => {
      if (this._ismounted) {
        this.props.setError(error ? error : 'Could not retrieve the floor info from the server.');
        this.setState({ isLoadingFloor: false, selectedRoom: null });
        this.updateFloorFromServer();
      }
    });
  }

  // Updates the floor regularly
  updateFloorFromServer() {
    if (isNull(this.floorTimer)) {
      this.floorTimer = setInterval(
        () => {
          // Clear the timer
          clearInterval(this.floorTimer);
          this.floorTimer = null;

          // Get the floor from the server or update the floor
          if (!this.state.isLoading) this.getFloorFromServer();
          else this.updateFloorFromServer();
        },
        5000
      );
    }
  }

  // Called when a user changes an individual room's alert status via an info panel
  handleSetRoomStatus = (newStatus) => {
    setRoomStatus(this.state.selectedRoom.getID(), newStatus).then(() => {
      this.state.selectedRoom.setAlertStatus(newStatus);
      this.state.selectedFloor.getRooms().forEach(room => {
        if (room.getID() === this.state.selectedRoom.getID()) room.setAlertStatus(newStatus);
      });
      this.setState({ selectedRoom: this.state.selectedRoom });

      // Clear the announcement banner if the room is normal
      if (newStatus === AlertTypes.NORMAL) this.props.clearAnnouncement(this.state.selectedRoom.getID());
    }).catch(error => {
      this.props.setError(error ? error : 'Could not set the room status.');
    });
  }

  render() {
    const rooms = this.state.selectedFloor?.getRooms() ?? [];
    const wide = this.props.windowWidth >= SPLIT_WIDTH;

    return (
      <>
        <PageHeader
          title='Live map'
          subtitle={rooms.length === 1 ? '1 room on this floor' : rooms.length + ' rooms on this floor'}
        />

        {/* The detail panel is an aside on wide viewports and replaces the map
            on narrow ones, where there is no room for both. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: wide ? 'minmax(0, 1fr) minmax(300px, 360px)' : 'minmax(0, 1fr)',
            gap: 24,
            alignItems: 'start'
          }}
        >
          {/* The design keeps the building and floor controls inside the map
              card rather than beside the page title — `showFloorControls` is
              false in its view model. */}
          <div style={{ minWidth: 0 }}>
            <section
              ref={this.mapRef}
              style={{
                scrollMarginTop: 24,
                background: T.surfaceCard,
                border: '1px solid ' + T.hairline,
                borderRadius: 8,
                padding: 24
              }}
            >
              {this.state.buildings &&
              <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid ' + T.hairlineSoft }}>
                <SelectorWidget
                  buildings={this.state.buildings}
                  onBuildingSelection={(building) => this.setState({ selectedBuilding: building, selectedFloorID: null })}
                  onFloorSelection={this.onFloorSelection}
                  selectedBuilding={this.state.selectedBuilding}
                  selectedFloorID={this.state.selectedFloorID}
                />
              </div>}

              <MapRoomView
                floorPlanImagePath={this.state.selectedFloorImage}
                rooms={rooms}
                onRoomSelection={this.onRoomSelection}
                selectedRoom={this.state.selectedRoom}
                isLoading={this.state.isLoading}
              />
              <StatusLegend />
            </section>

            <RoomList
              rooms={this.state.selectedFloor ? rooms : null}
              floorLabel={this.state.selectedFloor
                ? ['floor ' + this.state.selectedFloor.getIdentifier(), this.state.selectedBuilding?.getName()].filter(Boolean).join(', ')
                : null}
              selectedRoomID={this.state.selectedRoom?.getID() ?? null}
              onSelect={this.onRoomListSelection}
            />
          </div>

          <DetailAside
            windowWidth={this.props.windowWidth}
            open={this.state.selectedRoom != null}
            onClose={() => this.setState({ selectedRoom: null })}
          >
            {this.state.selectedRoom ?
              <RoomInfoPanel room={this.state.selectedRoom} onSetStatus={this.handleSetRoomStatus} handleClose={() => this.setState({ selectedRoom: null })} setupMode={this.props.setupMode} />
              :
              <DefaultInfoPanel message='Select a room on the map to see its details.' />
            }
          </DetailAside>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  isLoggedIn: state.auth.userID != null,
  isMobile: state.ui.isMobile,
  windowWidth: state.ui.windowSize.width,
  setupMode: state.setupMode.mode || state.setupMode.isLoading
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg),
  clearAnnouncement: (id) => dispatch(removeAnnouncement(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MapView));

MapView.propTypes = {
  isLoggedIn: PropTypes.bool,
  location: PropTypes.object,
  setError: PropTypes.func,
  isMobile: PropTypes.bool,
  windowWidth: PropTypes.number,
  setupMode: PropTypes.bool.isRequired,
  clearAnnouncement: PropTypes.func.isRequired
};
